import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";
import { createChangeOrderReducer } from "../../../SharedModule/utils/changeOrderReducer";
import { Client } from "../../types/clients-module-types";
import { SelectItem } from "../../../SharedModule/types/shared-module-types";
import { generateDistinctOwners } from "../../utils/clientsUtils";
import { UserWithProfileType } from "../../../UsersModule/types/users-module-types";
import { STATUS_TERMINATED } from "../../../SharedModule/utils/constants";

type ClientListState = {
  clientlist: Client[];
  clientOwnerList: SelectItem[];
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
  isShowingClients: boolean;
  filterText: string;
  filterOwnersSelected: SelectItem[];
  isClientsLoading: boolean;
  isClientsLoaded: boolean;
  actualClientForHeaderInfo: Client | null;
  isActualClientForHeaderInfoLoading: boolean;
  needRefresh: boolean;
  needHeaderRefresh: boolean;
  allUsersWithProfile: UserWithProfileType[];
  isLoadingAllUsersWithProfile: boolean;
  isLoadedAllUsersWithProfile: boolean;
};

const initialState: ClientListState = {
  clientlist: [],
  clientOwnerList: [],
  orderedBy: {
    orderBy: "companyName",
    criteria: "asc",
    dataType: "string",
  },
  isShowingClients: true,
  filterText: "",
  filterOwnersSelected: [],
  isClientsLoading: false,
  isClientsLoaded: false,
  actualClientForHeaderInfo: null,
  isActualClientForHeaderInfoLoading: false,
  needRefresh: false,
  needHeaderRefresh: false,
  allUsersWithProfile: [],
  isLoadingAllUsersWithProfile: false,
  isLoadedAllUsersWithProfile: false,
};

export const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    load: (state, action) => {
      state.clientlist = action.payload.data;
      state.clientOwnerList = generateDistinctOwners(action.payload.data);
      state.isClientsLoading = false;
      state.isClientsLoaded = true;
    },
    loadAllUsersWithProfile: (state, action) => {
      state.allUsersWithProfile = action.payload;
      state.isLoadingAllUsersWithProfile = false;
      state.isLoadedAllUsersWithProfile = true;
    },
    setAllUsersWithProfileLoading: (state, action) => {
      state.isLoadingAllUsersWithProfile = action.payload;
    },
    setAllUsersWithProfileLoaded: (state, action) => {
      state.isLoadedAllUsersWithProfile = action.payload;
    },
    setIsClientsLoading: (state, action) => {
      state.isClientsLoading = action.payload;
    },
    setIsClientsLoaded: (state, action) => {
      state.isClientsLoaded = action.payload;
    },
    setShowClients: (state, action) => {
      state.isShowingClients = action.payload;
    },
    setFilterText: (state, action) => {
      state.filterText = action.payload;
    },
    setFilterOwnersSelected: (state, action) => {
      state.filterOwnersSelected = action.payload;
    },
    resetFilters: (state) => {
      state.isShowingClients = initialState.isShowingClients;
      state.filterText = initialState.filterText;
      state.filterOwnersSelected = initialState.filterOwnersSelected;
    },
    setActualClientForHeaderInfo: (state, action) => {
      state.actualClientForHeaderInfo = action.payload;
      state.isActualClientForHeaderInfoLoading = false;
      state.needHeaderRefresh = false;
    },
    setIsActualClientForHeaderInfoLoading: (state, action) => {
      state.isActualClientForHeaderInfoLoading = action.payload;
    },
    setNeedRefresh: (state, action) => {
      state.needRefresh = action.payload;
    },
    setNeedHeaderRefresh: (state, action) => {
      state.needHeaderRefresh = action.payload;
    },
    changeOrder: createChangeOrderReducer("orderedBy"),
  },
});

// Action creators are generated for each case reducer function
export const {
  load,
  loadAllUsersWithProfile,
  setAllUsersWithProfileLoading,
  setAllUsersWithProfileLoaded,
  setIsClientsLoading,
  setIsClientsLoaded,
  setShowClients,
  setFilterText,
  setFilterOwnersSelected,
  resetFilters,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
  setNeedRefresh,
  setNeedHeaderRefresh,
  changeOrder,
} = clientsSlice.actions;

export const IsClientsLoading = (state: any) => {
  return state.clients.isClientsLoading;
};

export const IsClientsLoaded = (state: any) => {
  return state.clients.isClientsLoaded;
};

export const IsAllUsersWithProfileLoading = (state: any) => {
  return state.clients.isLoadingAllUsersWithProfile;
};

export const IsAllUsersWithProfileLoaded = (state: any) => {
  return state.clients.isLoadedAllUsersWithProfile;
};

export const SelectClients = ({ clients }: { clients: ClientListState }) => {
  return { clientlist: clients.clientlist };
};

export const SelectClientsIds = (state: any) => {
  return state.clients.clientlist.map((client) => client.id);
};

export const SelectClientsOwners = (state: any) => {
  return state.clients.clientOwnerList;
};

export const SelectAllUsersWithProfileInfo = (state: any) => {
  return state.clients.allUsersWithProfile
    .filter(
      (user) =>
        user.isActive &&
        user.userProfile?.employmentStatus !== STATUS_TERMINATED
    )
    .map((user) => ({ label: user.fullName, value: user.id }));
};

export const IsShowingClients = (state: any) => {
  return state.clients.isShowingClients;
};

export const SelectFilterText = (state: any) => {
  return state.clients.filterText;
};

export const SelectFilterOwnersSelected = (state: any) => {
  return state.clients.filterOwnersSelected;
};

export const SelectActualClientForHeaderInfo = (state: any) => {
  return state.clients.actualClientForHeaderInfo;
};

export const IsActualClientForHeaderInfoLoading = (state: any) => {
  return state.clients.isActualClientForHeaderInfoLoading;
};

export const SelectNeedRefresh = (state: any) => {
  return state.clients.needRefresh;
};

export const SelectNeedHeaderRefresh = (state: any) => {
  return state.clients.needHeaderRefresh;
};

export const SelectOrderCriteria = ({
  clients,
}: {
  clients: ClientListState;
}) => clients.orderedBy;

export const SelectOrderedBy = createSelector(
  [SelectClients, SelectOrderCriteria],
  (client, orderCriteria) => {
    return {
      clientlist:
        (client.clientlist &&
          sortDataBy(
            client.clientlist,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export default clientsSlice.reducer;
